import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const SessionManagement = ({ session, onRefreshToken, onSignOut }) => {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await onRefreshToken();
    setIsRefreshing(false);
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry - now;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="User" size={18} />
          Account Information
        </h3>
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-full overflow-hidden bg-muted flex-shrink-0">
              <Image
                src={session?.user?.avatar}
                alt={session?.user?.avatarAlt}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-lg font-semibold text-foreground truncate">{session?.user?.name}</h4>
              <p className="text-sm text-muted-foreground truncate">{session?.user?.email}</p>
              <div className="flex items-center gap-2 mt-1">
                <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
                <span className="text-xs text-success font-medium">Active Session</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Login Method</p>
              <div className="flex items-center gap-2">
                <Icon name="Mail" size={14} className="text-primary" />
                <p className="text-sm font-medium text-foreground">Google OAuth</p>
              </div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Session Started</p>
              <p className="text-sm font-medium text-foreground font-mono">{formatDate(session?.loginTime)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Token Expires In</p>
              <p className="text-sm font-medium text-foreground font-mono">{getTimeRemaining(session?.tokenExpiry)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Last Activity</p>
              <p className="text-sm font-medium text-foreground font-mono">{formatDate(session?.lastActivity)}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="RefreshCw"
              iconPosition="left"
              onClick={handleRefresh}
              loading={isRefreshing}
              className="flex-1"
            >
              Refresh Token
            </Button>
            <Button
              variant="destructive"
              size="sm"
              iconName="LogOut"
              iconPosition="left"
              onClick={onSignOut}
            >
              Sign Out
            </Button>
          </div>
        </div>
      </div>
      <div className="border-t border-border pt-6">
        <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Shield" size={18} />
          Security
        </h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-4 bg-muted rounded-md">
            <div className="flex items-center gap-3">
              <Icon name="Lock" size={18} className="text-success" />
              <div>
                <p className="text-sm font-medium text-foreground">Two-Factor Authentication</p>
                <p className="text-xs text-muted-foreground">Enabled via Google Account</p>
              </div>
            </div>
            <Icon name="CheckCircle2" size={18} className="text-success" />
          </div>

          <div className="flex items-center justify-between p-4 bg-muted rounded-md">
            <div className="flex items-center gap-3">
              <Icon name="Key" size={18} className="text-success" />
              <div>
                <p className="text-sm font-medium text-foreground">API Access</p>
                <p className="text-xs text-muted-foreground">Secure token-based authentication</p>
              </div>
            </div>
            <Icon name="CheckCircle2" size={18} className="text-success" />
          </div>

          <div className="flex items-center justify-between p-4 bg-muted rounded-md">
            <div className="flex items-center gap-3">
              <Icon name="Globe" size={18} className="text-success" />
              <div>
                <p className="text-sm font-medium text-foreground">HTTPS Connection</p>
                <p className="text-xs text-muted-foreground">All data encrypted in transit</p>
              </div>
            </div>
            <Icon name="CheckCircle2" size={18} className="text-success" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionManagement;